import { initializeApp } from "firebase/app";
import { getAnalytics } from "firebase/analytics";
import { 
  getAuth, 
  GoogleAuthProvider, 
  signInWithPopup, 
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut, 
  onAuthStateChanged,
  getMultiFactorResolver,
  PhoneAuthProvider,
  PhoneMultiFactorGenerator,
  RecaptchaVerifier,
  multiFactor,
  EmailAuthProvider,
  reauthenticateWithCredential
} from "firebase/auth";
import { getFirestore } from "firebase/firestore";
import { getStorage } from "firebase/storage";

const firebaseConfig = {
  apiKey: "AIzaSyC5Bg0VqwUulU6n03zQ0Y6XkF5eRGFqEN8",
  authDomain: "klorah-c4dc2.firebaseapp.com",
  projectId: "klorah-c4dc2",
  storageBucket: "klorah-c4dc2.appspot.com",
  messagingSenderId: "532276017665",
  appId: "1:532276017665:web:42b06f78bb5fcc13e7de00",
  measurementId: "G-F1MNVNGFR0"
};

// Helper function to detect if we are in development mode
const isDevelopment = () => {
  return process.env.NODE_ENV === 'development' || 
         window.location.hostname === 'localhost' || 
         window.location.hostname.includes('127.0.0.1');
};

// Test mode setting for development - only enable in development environments
const TEST_MODE = isDevelopment();

// Initialize Firebase
const app = initializeApp(firebaseConfig);

// Only initialize analytics in production
let analytics = null;
if (typeof window !== 'undefined' && process.env.NODE_ENV === 'production') {
  analytics = getAnalytics(app);
}

// Initialize Firebase services
const auth = getAuth(app);
const db = getFirestore(app);
const storage = getStorage(app);
const provider = new GoogleAuthProvider();

// Ensure Firebase auth has proper config
if (typeof window !== 'undefined') {
  // Only enable test mode settings in development environments
  if (TEST_MODE) {
    console.warn(
      "Firebase Phone Auth: TEST MODE is active! This should ONLY be used during development."
    );
    
    // Explicitly disable verification for testing - only in TEST_MODE
    auth.settings.appVerificationDisabledForTesting = true;
    
    // Make sure test mode is fully configured
    try {
      // Ensure cookies are enabled for reCAPTCHA
      document.cookie = "test_cookie=1; SameSite=None; Secure";
      console.log("Set test cookie for reCAPTCHA");
      
      // Ensure third-party cookies are not blocked
      if (!navigator.cookieEnabled) {
        console.warn("Cookies appear to be disabled. reCAPTCHA may not work properly.");
      }
    } catch (e) {
      console.warn("Could not set test cookie:", e);
    }
  } else {
    // In production, make sure verification is enabled
    auth.settings.appVerificationDisabledForTesting = false;
  }
  
  // Set the language to match user's browser
  try {
    auth.languageCode = navigator.language || 'en';
  } catch (error) {
    console.warn("Failed to set language code:", error);
  }
  
  // Log current setup for debugging
  const currentDomain = window.location.hostname;
  console.log("🔍 FIREBASE CONFIG DEBUG:");
  console.log("  - Current domain:", currentDomain);
  console.log("  - authDomain from config:", firebaseConfig.authDomain);
  console.log("  - App verification disabled:", auth.settings.appVerificationDisabledForTesting);
  console.log("  - NODE_ENV:", process.env.NODE_ENV);
  console.log("  - TEST_MODE:", TEST_MODE);
  console.log("  - isDevelopment():", isDevelopment());
  console.log("  - Firebase Project ID:", firebaseConfig.projectId);
}

// Cleanup any lingering reCAPTCHA instances that might be in the DOM
const cleanupExistingRecaptchas = () => {
  if (typeof window !== 'undefined' && document) {
    // Clean up any existing reCAPTCHA containers
    document.querySelectorAll('[id^="recaptcha-container"]').forEach(el => {
      try {
        el.innerHTML = '';
      } catch (error) {
        console.warn("Error cleaning reCAPTCHA container:", error);
      }
    });
    
    // Remove any reCAPTCHA badges/iframes that might be lingering
    document.querySelectorAll('.grecaptcha-badge').forEach(el => {
      try {
        el.parentNode.removeChild(el);
      } catch (error) {
        console.warn("Error removing reCAPTCHA badge:", error);
      }
    });
    
    // Also try to remove any iframe elements with recaptcha in their src
    document.querySelectorAll('iframe[src*="recaptcha"]').forEach(el => {
      try {
        el.parentNode.removeChild(el);
      } catch (error) {
        console.warn("Error removing reCAPTCHA iframe:", error);
      }
    });
  }
};

// Clean up on initialization
if (typeof window !== 'undefined') {
  // Run cleanup after DOM is fully loaded
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', cleanupExistingRecaptchas);
  } else {
    cleanupExistingRecaptchas();
  }
}

// Global variable to track active recaptcha instances
let activeRecaptchaVerifier = null;
let recaptchaContainerId = null;

// Define recaptchaConfig with necessary properties
const recaptchaConfig = {
  size: TEST_MODE ? 'normal' : 'invisible',
  callback: () => console.log('reCAPTCHA verified'),
  'expired-callback': () => console.log('reCAPTCHA expired'),
  // Add sitekey explicitly if in TEST_MODE
  ...(TEST_MODE && {
    sitekey: '6LeIxAcTAAAAAJcZVRqyHh71UMIEGNQ_MXjiZKhI' // This is Google's test key
  })
};

// Helper function to detect if running in mobile device or emulator
const isMobileDevice = () => {
  if (typeof window === 'undefined' || !window.navigator) return false;
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini|Mobile|mobile|CriOS/i.test(navigator.userAgent);
};

// Helper function to detect Chrome mobile emulator specifically
const isChromeMobileEmulator = () => {
  if (typeof window === 'undefined' || !window.navigator) return false;
  return /Chrome/.test(navigator.userAgent) && 
         /Mobile/.test(navigator.userAgent) &&
         (/Mac/.test(navigator.platform) || /Win/.test(navigator.platform));
};

// Helper function to safely create a RecaptchaVerifier instance
const createRecaptchaVerifier = async (containerId = 'recaptcha-container') => {
  try {
    console.log(`Attempting to create RecaptchaVerifier with containerId: ${containerId}`);
    
    // Check for mobile environment
    const mobile = isMobileDevice();
    const emulator = isChromeMobileEmulator();
    
    console.log(`Environment: ${mobile ? 'Mobile' : 'Desktop'}${emulator ? ' (Chrome Emulator)' : ''}`);
    
    // SPECIAL HANDLING: For Chrome Mobile Emulator in TEST_MODE
    // This is a workaround to avoid the auth/argument-error in the emulator
    if (TEST_MODE && (emulator || mobile)) {
      console.log("Using TEST_MODE RecaptchaVerifier bypass for mobile device");
      
      // Create a mock RecaptchaVerifier that mimics the real one but doesn't actually verify
      // This is ONLY for development/testing and should never be used in production
      return {
        render: () => Promise.resolve("mock-token-for-testing"),
        verify: () => Promise.resolve("mock-token-for-testing"),
        clear: () => Promise.resolve(),
        _isMockVerifier: true,
        getResponse: () => "mock-token-for-testing"
      };
    }
    
    // Ensure the container exists in the DOM
    const container = document.getElementById(containerId);
    if (!container) {
      // Create and append the container to the DOM if it doesn't exist
      const newContainer = document.createElement('div');
      newContainer.id = containerId;
      
      // Special styling for mobile devices to ensure visibility
      if (mobile || emulator) {
        newContainer.style.position = 'fixed';
        newContainer.style.top = '20px';
        newContainer.style.left = '20px';
        newContainer.style.right = '20px';
        newContainer.style.zIndex = '9999';
        newContainer.style.textAlign = 'center';
        newContainer.style.backgroundColor = 'rgba(255, 255, 255, 0.9)';
        newContainer.style.padding = '10px';
        newContainer.style.borderRadius = '8px';
        newContainer.style.boxShadow = '0 4px 6px rgba(0, 0, 0, 0.1)';
      }
      
      document.body.appendChild(newContainer);
      console.log(`Created new container with ID: ${containerId}`);
    }

    // Store for tracking purposes
    recaptchaContainerId = containerId;

    // Create a custom config for this specific instance
    let instanceConfig = { ...recaptchaConfig };
    
    // Override size for mobile
    if (mobile || emulator) {
      instanceConfig.size = 'normal';
      console.log('Using normal size for mobile device');
    }
    
    // Log the config we're using
    console.log('RecaptchaVerifier config:', JSON.stringify({
      size: instanceConfig.size,
      testMode: TEST_MODE,
      hasSitekey: !!instanceConfig.sitekey,
      callbacks: Object.keys(instanceConfig).filter(k => typeof instanceConfig[k] === 'function')
    }));

    // Create the RecaptchaVerifier instance with explicit parameters to avoid errors
    console.log(`Creating RecaptchaVerifier with explicit parameters`);
    const recaptchaVerifier = new RecaptchaVerifier(
      auth,
      containerId,
      instanceConfig
    );
    
    // Render it to initialize - use a try/catch specifically for the render step
    console.log('Rendering reCAPTCHA');
    try {
      await recaptchaVerifier.render();
      console.log('RecaptchaVerifier rendered successfully');
    } catch (renderError) {
      console.error("Error rendering reCAPTCHA:", renderError);
      // Try a fallback approach for render errors in mobile
      if (mobile || emulator) {
        console.log("Trying alternative approach for mobile render");
        // Force clear existing elements that might be causing conflicts
        document.querySelectorAll(`#${containerId} iframe, #${containerId} div`).forEach(el => {
          try { el.remove(); } catch (e) { /* ignore */ }
        });
        
        // Try again with a short delay
        await new Promise(resolve => setTimeout(resolve, 200));
        await recaptchaVerifier.render();
      } else {
        throw renderError;
      }
    }
    
    // Store for later cleanup
    activeRecaptchaVerifier = recaptchaVerifier;
    console.log('RecaptchaVerifier created and rendered successfully');
    
    return recaptchaVerifier;
  } catch (error) {
    console.error("❌ DEBUG: Error creating RecaptchaVerifier:", error);
    console.error("Error code:", error.code);
    console.error("Error message:", error.message);
    
    // Log detailed browser environment information
    console.log("Browser details:", {
      userAgent: navigator.userAgent,
      language: navigator.language,
      cookiesEnabled: navigator.cookieEnabled,
      doNotTrack: navigator.doNotTrack,
      platform: navigator.platform
    });
    
    // Check for known issues
    if (error.message && error.message.includes('Wrong number of parameters')) {
      console.error("❌ RecaptchaVerifier initialization parameter error. Check Firebase version compatibility.");
    } else if (error.message && error.message.includes('sitekey')) {
      console.error("❌ Missing or invalid sitekey. Make sure TEST_MODE is correctly configured.");
    } else if (error.message && error.message.toLowerCase().includes('cookie')) {
      console.error("❌ Cookie-related error. Third-party cookies may be blocked.");
    }
    
    // Special handling for argument-error, which is common in mobile environments
    if (error.code === 'auth/argument-error') {
      console.error("❌ Argument error creating RecaptchaVerifier. This is common in mobile environments.");
      
      // In non-TEST mode, we need to ensure this works, so try one more approach
      if (!TEST_MODE && (isMobileDevice() || isChromeMobileEmulator())) {
        try {
          console.log("Attempting alternative approach for production mobile...");
          
          // Clean up any existing instances
          await clearRecaptchaVerifier();
          
          // Create a fresh container with a new ID
          const fallbackId = `recaptcha-fallback-${Date.now()}`;
          const fallbackContainer = document.createElement('div');
          fallbackContainer.id = fallbackId;
          
          // Make the container very visible for mobile
          fallbackContainer.style.position = 'fixed';
          fallbackContainer.style.top = '50%';
          fallbackContainer.style.left = '50%';
          fallbackContainer.style.transform = 'translate(-50%, -50%)';
          fallbackContainer.style.zIndex = '99999';
          fallbackContainer.style.backgroundColor = 'white';
          fallbackContainer.style.padding = '20px';
          fallbackContainer.style.borderRadius = '8px';
          fallbackContainer.style.boxShadow = '0 4px 20px rgba(0, 0, 0, 0.15)';
          fallbackContainer.style.maxWidth = '90%';
          fallbackContainer.style.width = '320px';
          
          // Add instructions
          const instructions = document.createElement('div');
          instructions.innerHTML = '<p style="margin-bottom:10px;font-weight:bold;text-align:center;">Please complete the security verification</p>';
          fallbackContainer.appendChild(instructions);
          
          document.body.appendChild(fallbackContainer);
          
          // Use the most minimal configuration possible
          const fallbackConfig = {
            size: 'normal'
          };
          
          console.log("Creating last-resort RecaptchaVerifier for mobile");
          const fallbackVerifier = new RecaptchaVerifier(auth, fallbackId, fallbackConfig);
          await fallbackVerifier.render();
          
          return fallbackVerifier;
        } catch (fallbackError) {
          console.error("❌ All fallback attempts failed:", fallbackError);
        }
      }
    }
    
    // Try to clean up container on error
    try {
      if (containerId) {
        const container = document.getElementById(containerId);
        if (container) {
          document.body.removeChild(container);
        }
      }
    } catch (cleanupError) {
      console.warn("Error cleaning up container after failure:", cleanupError);
    }
    
    throw error;
  }
};

// Helper function to safely clear a RecaptchaVerifier instance
const clearRecaptchaVerifier = async (verifier = null) => {
  try {
    // If a specific verifier is passed, clear it
    if (verifier) {
      try {
        await verifier.clear();
        console.log('Cleared specific reCAPTCHA verifier instance');
      } catch (error) {
        console.warn("Error clearing specific reCAPTCHA verifier:", error);
      }
    }
    
    // Also clear the active verifier if it exists
    if (activeRecaptchaVerifier) {
      try {
        // Only try to clear if it's not already the one we cleared
        if (!verifier || verifier !== activeRecaptchaVerifier) {
          await activeRecaptchaVerifier.clear();
          console.log('Cleared active reCAPTCHA verifier instance');
        }
      } catch (error) {
        console.warn("Error clearing active reCAPTCHA verifier:", error);
      } finally {
        activeRecaptchaVerifier = null;
      }
    }
    
    // Clean up any lingering reCAPTCHA containers
    try {
      // First, try to clean our tracked container
      if (recaptchaContainerId) {
        const container = document.getElementById(recaptchaContainerId);
        if (container) {
          container.innerHTML = '';
          try {
            container.remove();
          } catch (e) {
            // If we can't remove it, at least hide it
            container.style.display = 'none';
          }
        }
        recaptchaContainerId = null;
      }
      
      // Also clean up any other reCAPTCHA containers that might be lingering
      document.querySelectorAll('[id^="recaptcha-container-"]').forEach(el => {
        try {
          el.innerHTML = '';
          el.remove();
        } catch (error) {
          console.warn("Error removing reCAPTCHA container:", error);
          // If we can't remove it, at least hide it
          el.style.display = 'none';
        }
      });
      
      // Clean up any reCAPTCHA iframes and divs created by the reCAPTCHA API
      document.querySelectorAll('iframe[src*="recaptcha"]').forEach(el => {
        try {
          el.remove();
        } catch (e) {
          console.warn("Error removing reCAPTCHA iframe:", e);
        }
      });
      
      document.querySelectorAll('.grecaptcha-badge').forEach(el => {
        try {
          el.remove();
        } catch (e) {
          console.warn("Error removing reCAPTCHA badge:", e);
        }
      });
    } catch (cleanupError) {
      console.warn("Error during reCAPTCHA container cleanup:", cleanupError);
    }
  } catch (error) {
    console.warn("Error in clearRecaptchaVerifier:", error);
  }
};

// Custom signOut function that handles any cleanup needed
const safeSignOut = async () => {
  // Perform any necessary cleanup before signing out
  try {
    // Clean up any subscriptions or listeners that might cause errors
    
    // Then sign out
    return await signOut(auth);
  } catch (error) {
    console.error("Error during sign out:", error);
    throw error;
  }
};

// Handler for MFA challenges
const handleMfaRequired = async (error) => {
  console.log('🔍 DEBUG: Handling MFA challenge');
  console.log('📝 DEBUG: MFA error code:', error.code);
  
  try {
    // Get resolver
    console.log('🔍 DEBUG: Getting MultiFactorResolver');
    const resolver = getMultiFactorResolver(auth, error);
    console.log('📝 DEBUG: MFA resolver obtained, hints count:', resolver.hints.length);
    
    // Store resolver for debugging
    console.log('📝 DEBUG: Resolver:', JSON.stringify({
      hints: resolver.hints.length,
      hasSession: !!resolver.session,
      auth: !!resolver.auth,
      methods: Object.keys(resolver)
    }));
    
    // Create RecaptchaVerifier instance using our helper
    console.log('🔍 DEBUG: Creating RecaptchaVerifier for MFA');
    const recaptchaVerifier = await createRecaptchaVerifier();
    
    console.log('📝 DEBUG: First MFA hint type:', resolver.hints[0]?.factorId);
    
    // Get user selected second factor (assuming phone for now)
    const phoneInfoOptions = {
      multiFactorHint: resolver.hints[0],
      session: resolver.session
    };
    console.log('📝 DEBUG: Created phoneInfoOptions with session');
    
    const phoneAuthProvider = new PhoneAuthProvider(auth);
    console.log('📝 DEBUG: Created PhoneAuthProvider');
    
    try {
      console.log('🔍 DEBUG: Calling verifyPhoneNumber for MFA');
      const verificationId = await phoneAuthProvider.verifyPhoneNumber(
        phoneInfoOptions, 
        recaptchaVerifier
      );
      
      console.log('✅ DEBUG: verifyPhoneNumber successful, got verificationId');
      
      // Clear recaptcha after use - don't rely on verifier._reset method
      console.log('🧹 DEBUG: Clearing RecaptchaVerifier after verification');
      try {
        if (recaptchaVerifier && typeof recaptchaVerifier.clear === 'function') {
          await recaptchaVerifier.clear();
        }
      } catch (clearError) {
        console.warn('⚠️ DEBUG: Error clearing recaptcha verifier:', clearError);
      }
      
      await clearRecaptchaVerifier(recaptchaVerifier);
      
      // Create a clean, serializable copy of the resolver
      const cleanResolver = {
        ...resolver,
        // Explicitly rebind the resolveSignIn method to fix "n._reset is not a function" error
        resolveSignIn: function(assertion) {
          return resolver.resolveSignIn(assertion);
        }
      };
      
      // Return data needed for verification prompt
      return {
        verificationId,
        resolver: cleanResolver
      };
    } catch (error) {
      console.error('❌ DEBUG: Error in MFA verification:', error);
      console.error('❌ DEBUG: Error code:', error.code);
      console.error('❌ DEBUG: Error message:', error.message);
      console.error('❌ DEBUG: Full error object:', JSON.stringify(error, Object.getOwnPropertyNames(error), 2));
      
      // Try to clear recaptcha on error
      console.log('🧹 DEBUG: Clearing RecaptchaVerifier after error');
      await clearRecaptchaVerifier(recaptchaVerifier);
      throw error;
    }
  } catch (error) {
    console.error('❌ DEBUG: Error in handleMfaRequired:', error);
    console.error('❌ DEBUG: Full error details:', JSON.stringify(error, Object.getOwnPropertyNames(error), 2));
    throw error;
  }
};

// Complete MFA sign-in
const completeMfaSignIn = async (verificationId, verificationCode, resolver) => {
  try {
    console.log("📝 DEBUG: Starting MFA sign-in completion");
    
    if (!resolver || typeof resolver.resolveSignIn !== 'function') {
      console.error("❌ DEBUG: Invalid resolver object", resolver);
      throw new Error("Authentication error: Invalid verification session. Please try signing in again.");
    }
    
    // Create credential with verification code
    const credential = PhoneAuthProvider.credential(verificationId, verificationCode);
    console.log("📝 DEBUG: Created phone credential");
    
    // Create multi-factor assertion
    const multiFactorAssertion = PhoneMultiFactorGenerator.assertion(credential);
    console.log("📝 DEBUG: Created multi-factor assertion");
    
    // Complete sign-in
    console.log("📝 DEBUG: Calling resolveSignIn");
    try {
      const result = await resolver.resolveSignIn(multiFactorAssertion);
      console.log("✅ DEBUG: Sign-in resolved successfully");
      return result;
    } catch (resolveError) {
      console.error("❌ DEBUG: Error in resolveSignIn:", resolveError);
      
      // Check for the specific _reset error
      if (resolveError.message && resolveError.message.includes('_reset is not a function')) {
        throw new Error("Authentication session expired. Please try signing in again.");
      }
      
      throw resolveError;
    }
  } catch (error) {
    console.error("❌ DEBUG: Error completing MFA sign-in:", error);
    
    // Provide more user-friendly error messages
    if (error.code === 'auth/invalid-verification-code') {
      throw new Error("The verification code you entered is invalid. Please try again.");
    } else if (error.code === 'auth/code-expired') {
      throw new Error("The verification code has expired. Please request a new code.");
    } else if (error.message && error.message.includes('_reset')) {
      throw new Error("Authentication session expired. Please try signing in again.");
    } else {
      throw error;
    }
  }
};

// Enroll a user in MFA
const enrollInMfa = async (user, phoneNumber) => {
  let recaptchaVerifier = null;
  
  try {
    // Check if email is verified first (required by Firebase for MFA)
    if (!user.emailVerified) {
      console.log("Email not verified - Firebase requires email verification before MFA enrollment");
      const error = new Error("Email verification is required before setting up 2FA. Please verify your email first.");
      error.code = 'auth/unverified-email';
      error.requiresEmailVerification = true;
      throw error;
    }
    
    // Initialize multi-factor for this user
    const userMultiFactor = multiFactor(user);
    
    // Start phone verification session
    const session = await userMultiFactor.getSession();
    
    // We always need to create the RecaptchaVerifier, even in TEST_MODE
    // The only difference is visibility and how errors are handled
    console.log(`Creating RecaptchaVerifier for phone number: ${phoneNumber} in ${TEST_MODE ? 'TEST_MODE' : 'PRODUCTION_MODE'}`);
    
    try {
      // Create a unique container ID
      const uniqueContainerId = `recaptcha-container-${Date.now()}`;
      
      // Ensure the container exists in the DOM
      let container = document.getElementById(uniqueContainerId);
      if (!container) {
        container = document.createElement('div');
        container.id = uniqueContainerId;
        // Set a visible style in TEST_MODE to ensure reCAPTCHA is shown
        if (TEST_MODE) {
          container.style.position = 'fixed';
          container.style.top = '10px';
          container.style.left = '10px';
          container.style.zIndex = '9999';
        }
        document.body.appendChild(container);
        console.log(`Created new container with ID: ${uniqueContainerId}`);
      }
      
      // Clean up any existing reCAPTCHA instances first
      await clearRecaptchaVerifier();
      
      // Create RecaptchaVerifier instance using our helper with the unique container ID
      recaptchaVerifier = await createRecaptchaVerifier(uniqueContainerId);
      
      // Check if we got a mock verifier (for mobile in TEST_MODE)
      const isMockVerifier = recaptchaVerifier._isMockVerifier === true;
      
      // For Chrome mobile emulator in TEST_MODE, use a special test approach
      if (isMockVerifier && TEST_MODE) {
        console.log("Using mock RecaptchaVerifier for testing");
        
        // Get a real session, but generate a fake verification ID
        // This is ONLY for development/testing
        
        // Check if this is a test phone number (supported in TEST_MODE)
        const isTestPhone = phoneNumber === '+15555555555' || 
                           phoneNumber === '+16505551234' || 
                           phoneNumber === '+14153219183';
                           
        // For test phone numbers in TEST_MODE, use a mock ID
        if (isTestPhone) {
          console.log("Using test phone number, returning fake verification ID");
          return `mock-verification-id-${Date.now()}`;
        } else {
          // For real numbers even in TEST_MODE, still try to send a real SMS
          console.log("Using real phone number in TEST_MODE, attempting to send real SMS");
        }
      }
      
      // Initialize phone auth provider
      const phoneAuthProvider = new PhoneAuthProvider(auth);
      
      // Request verification code
      console.log(`🔍 DEBUG: About to call phoneAuthProvider.verifyPhoneNumber for ${phoneNumber}`);
      console.log(`🔍 DEBUG: Session object:`, session);
      console.log(`🔍 DEBUG: RecaptchaVerifier object:`, recaptchaVerifier);
      
      // Add timeout to prevent hanging indefinitely
      const verificationPromise = phoneAuthProvider.verifyPhoneNumber({
        phoneNumber,
        session
      }, recaptchaVerifier);
      
      const timeoutPromise = new Promise((_, reject) => {
        setTimeout(() => {
          reject(new Error('SMS sending timed out after 30 seconds. This may be due to Firebase configuration issues or SMS provider problems.'));
        }, 30000); // 30 second timeout
      });
      
      console.log(`🔍 DEBUG: Starting verification with 30-second timeout...`);
      console.log(`🔍 DEBUG: Firebase Project: ${firebaseConfig.projectId}`);
      console.log(`🔍 DEBUG: Environment: ${process.env.NODE_ENV} (TEST_MODE: ${TEST_MODE})`);
      
      const verificationId = await Promise.race([verificationPromise, timeoutPromise]);
      
      console.log(`✅ DEBUG: phoneAuthProvider.verifyPhoneNumber completed successfully`);
      console.log(`✅ DEBUG: Received verificationId:`, verificationId ? 'Yes' : 'No');
      
      // Clear recaptcha after successful use
      try {
        await clearRecaptchaVerifier(recaptchaVerifier);
        console.log(`🔍 DEBUG: Successfully cleared reCAPTCHA verifier`);
      } catch (error) {
        console.warn("Error clearing reCAPTCHA after verification:", error);
      }
      recaptchaVerifier = null;
      
      // Log success
      console.log(`✅ SUCCESS: Verification code sent successfully to ${phoneNumber}`);
      console.log(`✅ SUCCESS: Returning verificationId: ${verificationId}`);
      return verificationId;
      
    } catch (recaptchaError) {
      console.error("❌ DEBUG: Error with SMS sending or reCAPTCHA verification:", recaptchaError);
      console.error("❌ DEBUG: Error code:", recaptchaError.code);
      console.error("❌ DEBUG: Error message:", recaptchaError.message);
      
      // Special handling for SMS-related errors
      if (recaptchaError.code === 'auth/operation-not-allowed') {
        console.error("❌ SMS sending not allowed. This usually means:");
        console.error("   1. SMS authentication is not enabled in Firebase Console");
        console.error("   2. The phone number region is not supported");
        console.error("   3. Firebase billing is not set up correctly");
        
        const enhancedError = new Error(
          "SMS sending is not enabled for this region. Please contact support or try a different phone number."
        );
        enhancedError.code = recaptchaError.code;
        enhancedError.handled = true;
        throw enhancedError;
      }
      
      // Special handling for timeout errors
      if (recaptchaError.message && recaptchaError.message.includes('timed out')) {
        console.error("❌ SMS sending timed out. This could be due to:");
        console.error("   1. Firebase configuration issues");
        console.error("   2. SMS provider problems");
        console.error("   3. Network connectivity issues");
        
        const enhancedError = new Error(
          "SMS sending timed out. This may be due to Firebase configuration or network issues. Please try again."
        );
        enhancedError.code = 'auth/timeout';
        enhancedError.handled = true;
        throw enhancedError;
      }
      
      // Special handling for auth/captcha-check-failed errors
      if (recaptchaError.code === 'auth/captcha-check-failed') {
        console.error("reCAPTCHA verification failed. This may be due to domain verification issues.");
        console.error(`Current domain: ${window.location.hostname}`);
        console.error(`Firebase authDomain: ${firebaseConfig.authDomain}`);
        
        // For mobile in TEST_MODE, allow proceeding anyway with a mock ID
        if (TEST_MODE && (isMobileDevice() || isChromeMobileEmulator())) {
          console.warn("In TEST_MODE on mobile, returning mock verification ID despite error");
          return `mock-verification-id-${Date.now()}`;
        }
        
        // Create a more user-friendly error
        const enhancedError = new Error(
          "Security verification failed. This could be due to domain verification issues."
        );
        enhancedError.code = recaptchaError.code;
        enhancedError.handled = true;
        throw enhancedError;
      }
      
      // Special handling for auth/argument-error on mobile
      if (recaptchaError.code === 'auth/argument-error' && 
          (isMobileDevice() || isChromeMobileEmulator()) && 
          TEST_MODE) {
        console.warn("In TEST_MODE on mobile, returning mock verification ID despite argument-error");
        return `mock-verification-id-${Date.now()}`;
      }
      
      // Clean up any failed reCAPTCHA
      if (recaptchaVerifier) {
        try {
          await clearRecaptchaVerifier(recaptchaVerifier);
        } catch (cleanupError) {
          console.warn("Error cleaning up failed reCAPTCHA:", cleanupError);
        }
        recaptchaVerifier = null;
      }
      
      throw recaptchaError;
    }
  } catch (error) {
    console.error("Error enrolling in MFA:", error);
    
    // Always attempt to clear the recaptcha verifier on error
    if (recaptchaVerifier) {
      try {
        await clearRecaptchaVerifier(recaptchaVerifier);
      } catch (cleanupError) {
        console.warn("Error cleaning up reCAPTCHA after failure:", cleanupError);
      }
    }
    
    // Enhanced error handling
    if (error.code === 'auth/unverified-email') {
      console.error("Email verification required before MFA enrollment");
      // The error is already properly formatted above, just re-throw it
      throw error;
    } else if (error.code === 'auth/billing-not-enabled') {
      console.error("Firebase Phone Authentication requires billing to be enabled.");
      console.log("Please visit https://console.firebase.google.com/project/_/settings/usage/details to verify your billing setup.");
      
      // Mark the error as handled to prevent duplicate error messages
      error.handled = true;
    } else if (error.code === 'auth/invalid-app-credential') {
      console.error("Invalid app credential. This is likely due to reCAPTCHA domain verification issues.");
      console.log("Please verify that your domain is authorized in the Firebase Console reCAPTCHA settings.");
      console.log("Current hostname:", window.location.hostname);
      
      // Mark the error as handled to prevent duplicate error messages
      error.handled = true;
      error.message = "Security verification failed. This could be due to domain verification issues.";
    }
    
    throw error;
  }
};

// Complete MFA enrollment
const completeMfaEnrollment = async (user, verificationId, verificationCode, displayName = "My Phone") => {
  try {
    if (!verificationId) {
      throw new Error("Verification session expired. Please try again.");
    }
    
    if (!verificationCode || verificationCode.length < 6) {
      throw new Error("Please enter a valid verification code.");
    }
    
    // Check if we're using a mock verification ID (for testing in mobile environments)
    if (TEST_MODE && verificationId.startsWith('mock-verification-id-')) {
      console.log("Using mock verification ID for testing - skipping actual MFA enrollment");
      
      // In TEST_MODE with a mock verification ID, we just simulate success
      // This is ONLY for development/testing and should never be used in production
      
      // The verification code should be "123456" in TEST_MODE for mock verification
      if (verificationCode === "123456") {
        console.log("Correct test verification code provided: 123456");
        return true;
      } else {
        console.log("Incorrect test verification code provided. In TEST_MODE, use 123456");
        // Even in test mode we want to show an error for wrong codes
        throw new Error("Invalid verification code. For test mode, use code 123456.");
      }
    }
    
    // Create credential with verification code
    const credential = PhoneAuthProvider.credential(verificationId, verificationCode);
    
    // Create multi-factor assertion
    const multiFactorAssertion = PhoneMultiFactorGenerator.assertion(credential);
    
    // Initialize multi-factor for this user
    const userMultiFactor = multiFactor(user);
    
    // Enroll the user in MFA
    await userMultiFactor.enroll(multiFactorAssertion, displayName);
    
    return true;
  } catch (error) {
    console.error("Error completing MFA enrollment:", error);
    
    // Provide more descriptive error messages
    if (error.code === 'auth/invalid-verification-code') {
      throw new Error("The verification code you entered is invalid. Please try again.");
    } else if (error.code === 'auth/code-expired') {
      throw new Error("The verification code has expired. Please request a new code.");
    } else if (error.message) {
      throw new Error(error.message);
    } else {
      throw new Error("Failed to enable two-factor authentication. Please try again.");
    }
  }
};

// Function to reauthenticate the user
const reauthenticateUser = async (password) => {
  try {
    const user = auth.currentUser;
    if (!user) {
      throw new Error("No user is currently signed in");
    }
    
    // Create credential with the user's email and password
    const credential = EmailAuthProvider.credential(user.email, password);
    
    // Reauthenticate with the credential
    await reauthenticateWithCredential(user, credential);
    return true;
  } catch (error) {
    console.error("Error reauthenticating user:", error);
    throw error;
  }
};

// Helper function to test if reCAPTCHA can be properly initialized
const testRecaptchaSetup = async () => {
  try {
    console.log("-------------------------------------");
    console.log("TESTING RECAPTCHA CONFIGURATION");
    console.log("-------------------------------------");
    console.log("Current domain:", window.location.hostname);
    console.log("Current URL:", window.location.href);
    console.log("Firebase Auth settings:", auth.settings);
    
    // First, create a temp container for the test
    const testContainerId = `recaptcha-test-${Date.now()}`;
    const container = document.createElement('div');
    container.id = testContainerId;
    container.style.position = 'fixed';
    container.style.bottom = '0';
    container.style.right = '0';
    container.style.zIndex = '9999';
    document.body.appendChild(container);
    
    console.log("Creating test reCAPTCHA instance...");
    
    // Create a visible recaptcha for testing
    const recaptchaVerifier = new RecaptchaVerifier(auth, testContainerId, {
      size: 'normal', // Make it visible for debugging
      callback: (response) => {
        console.log("reCAPTCHA successfully verified with response:", response ? "VALID" : "NONE");
        
        // Clean up after a successful test
        setTimeout(() => {
          try {
            recaptchaVerifier.clear();
            container.remove();
            console.log("Test reCAPTCHA cleaned up successfully");
          } catch (e) {
            console.warn("Error cleaning up test reCAPTCHA:", e);
          }
        }, 5000); // Give some time to see the success
      },
      'expired-callback': () => {
        console.log("Test reCAPTCHA expired");
      },
      'error-callback': (error) => {
        console.error("Test reCAPTCHA error:", error);
      }
    });
    
    // Try to render it
    await recaptchaVerifier.render();
    console.log("reCAPTCHA test render SUCCESSFUL - if you can see the widget below, your configuration is correct");
    console.log("-------------------------------------");
    console.log("MANUAL VERIFICATION REQUIRED: Check if you can see the reCAPTCHA widget at the bottom right corner");
    console.log("If visible, click on it to verify your setup is working properly");
    console.log("-------------------------------------");
    
    return true;
  } catch (error) {
    console.error("reCAPTCHA TEST FAILED:", error);
    console.error("Error details:", error.message, error.code);
    console.log("-------------------------------------");
    console.log("RECAPTCHA SETUP ISSUES DETECTED");
    console.log("Please check the following:");
    console.log("1. Your domain is added to the authorized domains in Firebase Console");
    console.log(`2. Your current domain (${window.location.hostname}) matches the authorized domain exactly`);
    console.log("3. You have billing enabled for your Firebase project");
    console.log("4. You've enabled Phone Authentication in Firebase Console");
    console.log("-------------------------------------");
    
    return false;
  }
};

// Function to diagnose Firebase Auth setup
const diagnoseFirebaseAuth = () => {
  console.group('🔍 FIREBASE AUTH DIAGNOSTIC REPORT');
  
  try {
    // Basic firebase initialization check
    console.log('📋 Firebase Initialized:', !!app);
    console.log('📋 Auth Initialized:', !!auth);
    
    // Environment checks
    console.log('📋 Node Environment:', process.env.NODE_ENV || 'not set');
    console.log('📋 Is Development Mode:', TEST_MODE);
    console.log('📋 Current Domain:', window.location.hostname);
    console.log('📋 Firebase AuthDomain:', firebaseConfig.authDomain);
    
    // Auth configuration
    console.log('📋 AppVerificationDisabled:', auth?.settings?.appVerificationDisabledForTesting || false);
    console.log('📋 Language Code:', auth?.languageCode);
    
    // User state
    console.log('📋 Current User:', auth?.currentUser ? `Signed in (${auth.currentUser.email})` : 'No user signed in');
    
    // Recaptcha status
    console.log('📋 Active RecaptchaVerifier:', !!activeRecaptchaVerifier);
    console.log('📋 Recaptcha Container ID:', recaptchaContainerId || 'none');
    
    // Check for related DOM elements
    const recaptchaElements = document.querySelectorAll('[id^="recaptcha"], .grecaptcha-badge, iframe[src*="recaptcha"]');
    console.log('📋 Recaptcha DOM Elements:', recaptchaElements.length);
    
    // Check for common issues
    const domainMismatch = firebaseConfig.authDomain !== window.location.hostname && 
                           !TEST_MODE && 
                           window.location.hostname !== 'localhost';
    
    if (domainMismatch) {
      console.warn('⚠️ Potential domain mismatch issue: The current domain does not match the authDomain in Firebase config');
    }
    
    // Final assessment
    console.log('📋 TEST_MODE active:', TEST_MODE ? 'Yes (recaptcha bypass enabled)' : 'No (standard verification)');
    
    return {
      initialized: !!app && !!auth,
      testMode: TEST_MODE,
      domainMismatch,
      user: !!auth?.currentUser,
      recaptchaActive: !!activeRecaptchaVerifier
    };
  } catch (error) {
    console.error('❌ Error during diagnosis:', error);
    return { error: error.message };
  } finally {
    console.groupEnd();
  }
};

// Helper function to diagnose reCAPTCHA issues
const diagnoseRecaptchaIssues = () => {
  console.log("-------------------------------------");
  console.log("DIAGNOSING RECAPTCHA ISSUES");
  console.log("-------------------------------------");
  
  const issues = [];
  
  // Check if in iframe
  if (window.self !== window.top) {
    console.warn("🚨 Running in an iframe - reCAPTCHA may not work properly");
    issues.push("The app is running inside an iframe, which can cause reCAPTCHA to fail");
  }
  
  // Check cookies
  if (!navigator.cookieEnabled) {
    console.warn("🚨 Cookies are disabled - reCAPTCHA requires cookies");
    issues.push("Cookies are disabled in your browser. Enable cookies for reCAPTCHA to work");
  }
  
  // Check for cookie blocking
  try {
    document.cookie = "recaptcha_test=1; path=/";
    if (document.cookie.indexOf("recaptcha_test") === -1) {
      console.warn("🚨 Unable to set cookies - third-party cookies may be blocked");
      issues.push("Your browser is blocking cookies needed for reCAPTCHA");
    }
  } catch (e) {
    console.warn("🚨 Error testing cookies:", e);
    issues.push("There was an error testing cookie functionality");
  }
  
  // Check local storage
  try {
    localStorage.setItem('recaptcha_test', '1');
    localStorage.removeItem('recaptcha_test');
  } catch (e) {
    console.warn("🚨 localStorage is not available:", e);
    issues.push("Local storage is disabled or not available");
  }
  
  // Check for browser compatibility issues
  const userAgent = navigator.userAgent.toLowerCase();
  if (userAgent.indexOf('msie') !== -1 || userAgent.indexOf('trident') !== -1) {
    console.warn("🚨 Using Internet Explorer - reCAPTCHA may have compatibility issues");
    issues.push("Internet Explorer has limited support for reCAPTCHA");
  }
  
  // Check for privacy extensions
  const checkForPrivacyExtensions = () => {
    // Simple timing-based detection for some ad blockers
    const start = performance.now();
    setTimeout(() => {
      const elapsed = performance.now() - start;
      if (elapsed < 4) {
        console.warn("🚨 Possible privacy extension detected - might interfere with reCAPTCHA");
        issues.push("A privacy extension or ad blocker may be interfering with reCAPTCHA");
      }
    }, 5);
  };
  
  checkForPrivacyExtensions();
  
  console.log("Potential issues found:", issues.length > 0 ? issues.length : "None");
  return issues;
};

// Email verification functions
const sendEmailVerification = async (user) => {
  try {
    const { sendEmailVerification: firebaseSendEmailVerification } = await import('firebase/auth');
    
    await firebaseSendEmailVerification(user, {
      url: window.location.origin + '/app/onboarding',
      handleCodeInApp: false
    });
    
    console.log('Email verification sent successfully');
    return true;
  } catch (error) {
    console.error('Error sending email verification:', error);
    throw new Error(`Failed to send verification email: ${error.message}`);
  }
};

const checkEmailVerified = async (user) => {
  try {
    // Reload the user to get the latest email verification status
    await user.reload();
    return user.emailVerified;
  } catch (error) {
    console.error('Error checking email verification status:', error);
    return false;
  }
};

const waitForEmailVerification = async (user, maxAttempts = 30, intervalMs = 2000) => {
  let attempts = 0;
  
  while (attempts < maxAttempts) {
    const isVerified = await checkEmailVerified(user);
    if (isVerified) {
      console.log('Email verification confirmed');
      return true;
    }
    
    attempts++;
    console.log(`Checking email verification... (${attempts}/${maxAttempts})`);
    
    if (attempts < maxAttempts) {
      await new Promise(resolve => setTimeout(resolve, intervalMs));
    }
  }
  
  console.log('Email verification check timed out');
  return false;
};

export { 
  app, 
  analytics, 
  auth, 
  db, 
  storage,
  GoogleAuthProvider,
  signInWithPopup,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  safeSignOut,
  onAuthStateChanged,
  provider,
  handleMfaRequired,
  completeMfaSignIn,
  enrollInMfa,
  completeMfaEnrollment,
  multiFactor,
  RecaptchaVerifier,
  reauthenticateUser,
  createRecaptchaVerifier,
  clearRecaptchaVerifier,
  testRecaptchaSetup,
  sendEmailVerification,
  checkEmailVerified,
  waitForEmailVerification,
  TEST_MODE,
  diagnoseFirebaseAuth,
  diagnoseRecaptchaIssues,
  isMobileDevice,
  isChromeMobileEmulator
}; 

export default app; 